/**
 * File / Folder name input
 *
 * 2 actions :
 * - **create new folder** : (default) when not provide `file` input
 * - **rename a selected file** : when `file` input is provided
 */
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';

import {
  ButtonComponent,
  InputComponent,
  Maybe,
  ModalComponent,
  ModalService,
  ValueOf,
} from '@evc/web-components';
import { EvcFormService } from '@shared/reactive-form/reactive-form.service';
import type { DataToFormModel } from '@shared/reactive-form/types/reactive-form.type';
import { I18nService, TranslatePipe } from '@shared/services/i18n/i18n.service';

import type { FileItem } from '@app/types/file-item.type';

export const FileFormModalActions = {
  CREATE: 'create',
  RENAME: 'rename',
} as const;
export type FileFormModalAction = ValueOf<typeof FileFormModalActions>;

export type FileFormFormData = {
  name: string;
};
export type FileFormFormModel = DataToFormModel<FileFormFormData>;

@Component({
  selector: 'modal-file-form',
  templateUrl: './file-form.component.html',
  styleUrls: ['./file-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    InputComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  providers: [EvcFormService],
})
export class FileFormModalComponent extends ModalComponent implements OnInit, AfterViewInit {
  file = input<Maybe<FileItem>>(undefined);
  action = input.required<FileFormModalAction>();

  form!: FormGroup<FileFormFormModel>;
  formDirective = viewChild.required(FormGroupDirective);
  field = viewChild.required<InputComponent>('field');

  // ng limitation : must use child use child ModalComponent to provide ng-containers
  // so in this parent instance we will need to forward subject emitions
  childModal = viewChild.required(ModalComponent);

  readonly #modalService = inject(ModalService);
  readonly #i18nService = inject(I18nService);
  readonly #formService = inject(EvcFormService<FileFormFormData, boolean>);
  readonly #formBuilder = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);

  defaultFilename = computed(() => this.file()?.name || this.#i18nService.t('file.defaultName'));

  i18n = computed(() => {
    const action = this.action();

    return {
      title: `actions.${action}.label`,
      action: `actions.${action}.action`,
      placeholder: `actions.${action}.placeholder`,
      cancel: 'actions.cancel',
    };
  });

  submit = this.#formService.submit.bind(this.#formService);

  ngOnInit(): void {
    const control = this.#formService.controlFactory(this.#formBuilder.nonNullable, {
      name: this.defaultFilename(),
    });

    this.form = this.#formBuilder.group<FileFormFormModel>({
      name: control('name'),
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.#bindFormService();
    this.#bindChildModalSubject();

    setTimeout(() => {
      this.field()?.nativeElement?.select();
    }, 0);
  }

  submit$({ name }: FileFormFormData): Observable<unknown> {
    setTimeout(() => {
      this.subject.next({ ...this.file(), name });
    }, 0);

    return this.subject;
  }

  #bindChildModalSubject(): void {
    const child = this.childModal();
    if (child && child !== this) {
      // Forward all child modal events to parent subject
      child.subject.subscribe({
        next: (value) => this.subject.next(value),
        error: (error) => this.subject.error(error),
        complete: () => this.subject.complete(),
      });
    }
  }

  #bindFormService(): void {
    this.#formService.bind({
      formGroup: this.form,
      formDirective: this.formDirective(),
      destroyRef: this.#destroyRef,
      onSuccess: this.#modalService.close.bind(this.#modalService),
      catchErrors: false,
      submitHook: this.submit$.bind(this),
    });
  }
}
